import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, ShieldCheck } from "lucide-react";
import { permissionsApi } from "../../api/permissions";
import { LoadingText, Toggle } from "../ui";
import type { DataGroup } from "../../types/user";

/* ── Types ───────────────────────────────────────────────────────── */

type FeatureMap = Record<string, string>; // key → display name
type PermMatrix = Record<string, Record<string, boolean>>; // role → feature → enabled

type Datasource = { key: string; name: string };

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  analyst: "Analyst",
  viewer: "Viewer",
};

/* ── Data group form modal ───────────────────────────────────────── */

function DataGroupModal({
  group,
  datasources,
  onSave,
  onClose,
}: {
  group?: DataGroup;
  datasources: Datasource[];
  onSave: (name: string, keys: string[]) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(group?.name ?? "");
  const [keys, setKeys] = useState<string[]>(group?.datasource_keys ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function toggleKey(k: string) {
    setKeys((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError("Name is required"); return; }
    if (keys.length === 0) { setError("Select at least one datasource"); return; }
    setSaving(true);
    try {
      await onSave(name.trim(), keys);
      onClose();
    } catch { return; } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-sm rounded-xl border border-gray-200 bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            {group ? "Edit data group" : "New data group"}
          </h3>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:text-gray-600">
            <X size={15} />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">Group name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Phoenix Only"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="mb-2 block text-xs font-medium text-gray-700">Datasource access</label>
            {datasources.length === 0 ? (
              <p className="text-xs text-gray-400">No datasources configured.</p>
            ) : (
              <div className="space-y-2">
                {datasources.map((ds) => (
                  <label key={ds.key} className="flex cursor-pointer items-center gap-2.5">
                    <input
                      type="checkbox"
                      checked={keys.includes(ds.key)}
                      onChange={() => toggleKey(ds.key)}
                      className="h-4 w-4 rounded border-gray-300 text-[#1D345F]"
                    />
                    <span className="text-sm text-gray-800">{ds.name}</span>
                    <span className="text-xs text-gray-400">({ds.key})</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="rounded-lg bg-[#1D345F] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#162847] disabled:opacity-50">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────────── */

export default function PermissionsSettings() {
  const [matrix, setMatrix] = useState<PermMatrix>({});
  const [features, setFeatures] = useState<FeatureMap>({});
  const [groups, setGroups] = useState<DataGroup[]>([]);
  const [datasources, setDatasources] = useState<Datasource[]>([]);
  const [matrixLoading, setMatrixLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null); // "role:feature"
  const [groupModal, setGroupModal] = useState<{ group?: DataGroup } | null>(null);
  const [deletingGroup, setDeletingGroup] = useState<string | null>(null);

  async function loadMatrix() {
    setMatrixLoading(true);
    try {
      const res = await permissionsApi.getRoleMatrix();
      setMatrix(res.data.permissions);
      setFeatures(res.data.features);
    } catch { return; } finally {
      setMatrixLoading(false);
    }
  }

  async function loadGroups() {
    try {
      const res = await permissionsApi.getDataGroups();
      setGroups(res.data);
    } catch { /* ignore */ }
  }

  async function loadDatasources() {
    try {
      const res = await permissionsApi.getDatasources();
      setDatasources(res.data);
    } catch { /* ignore */ }
  }

  useEffect(() => {
    loadMatrix();
    loadGroups();
    loadDatasources();
  }, []);

  async function togglePermission(role: string, feature: string, enabled: boolean) {
    const key = `${role}:${feature}`;
    setSaving(key);
    // Optimistic update
    setMatrix((prev) => ({
      ...prev,
      [role]: { ...prev[role], [feature]: enabled },
    }));
    try {
      await permissionsApi.updateRolePermission(role, feature, enabled);
    } catch {
      // Revert on error
      setMatrix((prev) => ({
        ...prev,
        [role]: { ...prev[role], [feature]: !enabled },
      }));
    } finally {
      setSaving(null);
    }
  }

  async function saveGroup(name: string, keys: string[]) {
    if (groupModal?.group) {
      await permissionsApi.updateDataGroup(groupModal.group.id, { name, datasource_keys: keys });
    } else {
      await permissionsApi.createDataGroup({ name, datasource_keys: keys });
    }
    await loadGroups();
  }

  async function deleteGroup(id: string) {
    setDeletingGroup(id);
    try {
      await permissionsApi.deleteDataGroup(id);
      setGroups((prev) => prev.filter((g) => g.id !== id));
    } catch { return; } finally {
      setDeletingGroup(null);
    }
  }

  const roles = ["admin", "analyst", "viewer"];

  return (
    <div className="space-y-10 max-w-3xl">
      {/* ── Role feature access ── */}
      <section>
        <h2 className="text-base font-semibold text-gray-900">Feature access by role</h2>
        <p className="mt-1 mb-4 text-sm text-gray-500">
          Control which application features are available to each role. Changes take effect immediately for all users with that role. Super Admin always has full access.
        </p>

        {matrixLoading ? (
          <LoadingText />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-400">
                    Feature
                  </th>
                  <th className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-400">
                    Super Admin
                  </th>
                  {roles.map((r) => (
                    <th key={r} className="px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-400">
                      {ROLE_LABELS[r]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {Object.entries(features).map(([key, label]) => (
                  <tr key={key}>
                    <td className="px-5 py-3.5 font-medium text-gray-800">{label}</td>
                    {/* Super Admin — always on, locked */}
                    <td className="px-5 py-3.5 text-center">
                      <span className="inline-flex items-center justify-center">
                        <ShieldCheck size={16} className="text-[#1D345F]" />
                      </span>
                    </td>
                    {roles.map((role) => {
                      const savingKey = `${role}:${key}`;
                      const enabled = matrix[role]?.[key] ?? true;
                      return (
                        <td key={role} className="px-5 py-3.5 text-center">
                          <div className="flex justify-center">
                            <Toggle
                              checked={enabled}
                              onChange={(v) => togglePermission(role, key, v)}
                              disabled={saving === savingKey}
                            />
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Data access groups ── */}
      <section>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Data access groups</h2>
            <p className="mt-1 text-sm text-gray-500">
              Define named groups that restrict which datasources a user can query. Assign groups to individual users in the Users tab.
            </p>
          </div>
          <button
            onClick={() => setGroupModal({})}
            className="flex shrink-0 items-center gap-1.5 rounded-lg bg-[#1D345F] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#162847]"
          >
            <Plus size={14} />
            New group
          </button>
        </div>

        {groups.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 px-5 py-8 text-center">
            <p className="text-sm text-gray-400">No data access groups configured.</p>
            <p className="mt-1 text-xs text-gray-400">
              Create a group to restrict users to specific datasources.
            </p>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 bg-white">
            {groups.map((group) => (
              <div key={group.id} className="flex items-center justify-between gap-4 px-5 py-3.5">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800">{group.name}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {group.datasource_keys.length === 0 ? (
                      <span className="text-xs text-gray-400">No datasources</span>
                    ) : (
                      group.datasource_keys.map((k) => {
                        const ds = datasources.find((d) => d.key === k);
                        return (
                          <span key={k} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                            {ds?.name ?? k}
                          </span>
                        );
                      })
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    onClick={() => setGroupModal({ group })}
                    className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => deleteGroup(group.id)}
                    disabled={deletingGroup === group.id}
                    className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-40"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {groupModal && (
        <DataGroupModal
          group={groupModal.group}
          datasources={datasources}
          onSave={saveGroup}
          onClose={() => setGroupModal(null)}
        />
      )}
    </div>
  );
}
